import React, { useState, useEffect } from 'react';
import './PDFDesignModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowLeft, faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { secureFetch } from '../../../utils/secureFetch';
import PDFLayoutStep from './PDFLayoutStep';
import PDFStyleStep from './PDFStyleStep';
import PDFContentStep from './PDFContentStep';
import PDFSummaryStep from './PDFSummaryStep';
import PDFGenerateStep from './PDFGenerateStep';

const PDFDesignModal = ({ isOpen, onClose, catalog, providerInfo }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [productCount, setProductCount] = useState(0);
    const [hasGeneratedPDF, setHasGeneratedPDF] = useState(false); // Flag para evitar regeneración
    const [generatedPdfUrl, setGeneratedPdfUrl] = useState(null); // URL del PDF generado
    const [, setIsGenerating] = useState(false); // eslint-disable-line no-unused-vars
    const [pdfGenerationKey] = useState(0); // Key para forzar re-render
    const [pdfConfig, setPdfConfig] = useState({
        layout: null, // 'alternado', 'doble-columna', 'grid-3x3', 'magazine'
        colorPalette: 'orange', // ID de la paleta seleccionada
        font: 'modern', // ID de la fuente seleccionada
        coverType: 'generated', // 'banner' o 'generated'
        showPrices: true,
        showDescriptions: true,
        showCodes: true,  // ✅ Cambiado a true
        showContactPage: true,
        removeBackgrounds: false,
        colorScheme: {
            primary: '#fa7e17',
            secondary: '#333',
            background: '#fff'
        },
        template: 'modern', // 'modern', 'classic', 'minimal'
        showLogo: true,
        showContactInfo: true
    });

    const totalSteps = 5;

    // Resetear al abrir y obtener cantidad de productos
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(1);
            setHasGeneratedPDF(false); // Resetear flag de generación
            setGeneratedPdfUrl(null); // Resetear URL del PDF
            setIsGenerating(false); // Resetear estado de generación
            setPdfConfig({
                layout: null,
                colorPalette: 'orange',
                font: 'modern',
                coverType: 'generated',
                showPrices: true,
                showDescriptions: true,
                showCodes: true,  // ✅ Cambiado a true
                showContactPage: true,
                removeBackgrounds: false,
                colorScheme: {
                    primary: '#fa7e17',
                    secondary: '#333',
                    background: '#fff'
                },
                template: 'modern',
                showLogo: true,
                showContactInfo: true
            });
            
            // Obtener cantidad de productos del catálogo
            const fetchProductCount = async () => {
                try {
                    const response = await secureFetch(`https://api.minymol.com/catalog/${catalog.id}/product-count`);
                    const data = await response.json();
                    setProductCount(data.count || 0);
                } catch (error) {
                    console.error('Error al obtener cantidad de productos:', error);
                    setProductCount(0);
                }
            };

            if (catalog?.id) {
                fetchProductCount();
            }
        }
    }, [isOpen, catalog?.id]); // Cambiar dependencia para evitar re-renders innecesarios

    if (!isOpen) return null;

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleConfigChange = (field, value) => {
        setPdfConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const canProceed = () => {
        switch (currentStep) {
            case 1:
                return pdfConfig.layout !== null;
            case 2:
                return true; // Siempre puede avanzar desde estilo
            case 3:
                return true; // Siempre puede avanzar desde contenido
            case 4:
                return true; // Siempre puede avanzar desde preview
            case 5:
                return true;
            default:
                return false;
        }
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case 1:
                return 'Selecciona el diseño de tu catálogo';
            case 2:
                return 'Personaliza el estilo visual';
            case 3:
                return 'Configura el contenido';
            case 4:
                return 'Revisa y confirma';
            case 5:
                return 'Generando tu catálogo PDF';
            default:
                return '';
        }
    };

    const handleEditStep = (step) => {
        setCurrentStep(step);
    };

    const handleGeneratePDF = () => {
        setCurrentStep(5);
    };

    const handlePDFGenerated = (pdfUrl) => {
        console.log('PDF generado con URL:', pdfUrl);
        setHasGeneratedPDF(true);
        setGeneratedPdfUrl(pdfUrl);
        setIsGenerating(false);
    };

    return (
        <div className="pdf-design-modal-overlay" onClick={onClose}>
            <div className="pdf-design-modal-container" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="pdf-design-modal-header">
                    <div className="pdf-design-modal-header-content">
                        <h2>Diseñar Catálogo PDF</h2>
                        <p className="pdf-design-catalog-name">{catalog?.name}</p>
                    </div>
                    <button 
                        className="pdf-design-modal-close-btn" 
                        onClick={onClose}
                        aria-label="Cerrar modal"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Progress Steps */}
                <div className="pdf-design-progress-container">
                    {[1, 2, 3, 4, 5].map((step) => (
                        <div key={step} className="pdf-design-progress-step-wrapper">
                            <div 
                                className={`pdf-design-progress-step ${
                                    step === currentStep ? 'active' : 
                                    step < currentStep ? 'completed' : ''
                                }`}
                            >
                                <div className="pdf-design-progress-step-number">
                                    {step < currentStep ? (
                                        <FontAwesomeIcon icon={faCheck} />
                                    ) : (
                                        step
                                    )}
                                </div>
                                <div className="pdf-design-progress-step-label">
                                    {step === 1 && 'Diseño'}
                                    {step === 2 && 'Estilo'}
                                    {step === 3 && 'Contenido'}
                                    {step === 4 && 'Revisar'}
                                    {step === 5 && 'Generar'}
                                </div>
                            </div>
                            {step < 5 && <div className="pdf-design-progress-line" />}
                        </div>
                    ))}
                </div>

                {/* Step Title */}
                <div className="pdf-design-step-title">
                    <h3>{getStepTitle()}</h3>
                </div>

                {/* Step Content */}
                <div className="pdf-design-modal-body">
                    {currentStep === 1 && (
                        <PDFLayoutStep 
                            selectedLayout={pdfConfig.layout}
                            onLayoutSelect={(layout) => handleConfigChange('layout', layout)}
                        />
                    )}
                    {currentStep === 2 && (
                        <PDFStyleStep 
                            selectedLayout={pdfConfig.layout}
                            selectedColor={pdfConfig.colorPalette}
                            onColorSelect={(color) => handleConfigChange('colorPalette', color)}
                            selectedFont={pdfConfig.font}
                            onFontSelect={(font) => handleConfigChange('font', font)}
                        />
                    )}
                    {currentStep === 3 && (
                        <PDFContentStep 
                            catalog={catalog}
                            coverType={pdfConfig.coverType}
                            onCoverTypeChange={(type) => handleConfigChange('coverType', type)}
                            showPrices={pdfConfig.showPrices}
                            onShowPricesChange={(value) => handleConfigChange('showPrices', value)}
                            showDescriptions={pdfConfig.showDescriptions}
                            onShowDescriptionsChange={(value) => handleConfigChange('showDescriptions', value)}
                            showCodes={pdfConfig.showCodes}
                            onShowCodesChange={(value) => handleConfigChange('showCodes', value)}
                            showContactPage={pdfConfig.showContactPage}
                            onShowContactPageChange={(value) => handleConfigChange('showContactPage', value)}
                            removeBackgrounds={pdfConfig.removeBackgrounds}
                            onRemoveBackgroundsChange={(value) => handleConfigChange('removeBackgrounds', value)}
                            selectedColorPalette={pdfConfig.colorPalette}
                        />
                    )}
                    {currentStep === 4 && (
                        <PDFSummaryStep 
                            pdfConfig={pdfConfig}
                            catalog={catalog}
                            productCount={productCount}
                            onEditStep={handleEditStep}
                        />
                    )}
                    {currentStep === 5 && !hasGeneratedPDF && (
                        <PDFGenerateStep 
                            pdfConfig={pdfConfig}
                            catalog={catalog}
                            onGenerateComplete={handlePDFGenerated}
                            key={`pdf-gen-${pdfGenerationKey}`}
                        />
                    )}
                    {currentStep === 5 && hasGeneratedPDF && generatedPdfUrl && (
                        <div className="pdf-generate-step-container">
                            <div className="pdf-generate-status success">
                                <div className="pdf-generate-icon success">
                                    <FontAwesomeIcon icon={faCheck} />
                                </div>
                                <h3>¡PDF generado exitosamente!</h3>
                                <p>Tu catálogo está listo para descargar</p>

                                <div className="pdf-generate-actions">
                                    <button 
                                        className="pdf-generate-btn primary"
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = generatedPdfUrl;
                                            link.download = `${catalog?.name || 'catalogo'}.pdf`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faCheck} />
                                        Descargar PDF
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Navigation */}
                <div className="pdf-design-modal-footer">
                    {currentStep < 5 && (
                        <>
                            <button
                                className="pdf-design-btn pdf-design-btn-secondary"
                                onClick={currentStep === 1 ? onClose : handleBack}
                            >
                                {currentStep === 1 ? (
                                    'Cancelar'
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                        <span>Atrás</span>
                                    </>
                                )}
                            </button>

                            <button
                                className="pdf-design-btn pdf-design-btn-primary"
                                onClick={currentStep === 4 ? handleGeneratePDF : handleNext}
                                disabled={!canProceed()}
                            >
                                {currentStep === 4 ? (
                                    <>
                                        <FontAwesomeIcon icon={faCheck} />
                                        <span>Generar PDF</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Siguiente</span>
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </>
                                )}
                            </button>
                        </>
                    )}
                    
                    {currentStep === 5 && (
                        <button
                            className="pdf-design-btn pdf-design-btn-secondary"
                            onClick={onClose}
                            style={{ margin: '0 auto' }}
                        >
                            Cerrar
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default PDFDesignModal;
